import Joi from 'joi';
import statusTicket from '../constant/status.json';
import { NotificationPreferenceEnum } from 'Modules/Auth/interfaces/auth.interface';

const normalizeString = (value: string) => {
    if (typeof value !== 'string') {
        throw new Error('Value must be a string');
    }
    return value.trim().replace(/\s+/g, '_').toLowerCase();
};


const certificateSchema = Joi.object({
    name: Joi.string().required().label('Certificate name').messages({
        'string.empty': '{{#label}} is empty',
        'any.required': '{{#label}} is required',
    }),
    file: Joi.string().required().label('Certificate file').messages({
        'string.empty': '{{#label}} is empty',
        'any.required': '{{#label}} is required',
    }),
});
const allowedSkills = Object.values(statusTicket.Skills).map(s => s.toLowerCase());

const identificationSchema = Joi.object({
    type: Joi.string()
        .required()
        .label('Identification type')
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    file: Joi.string()
        .required()
        .label('Identification file')
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    issuedAt: Joi.date()
        .optional()
        .label('Issued date')
        .messages({
            'date.base': '{{#label}} must be a valid date',
        }),
    // .when('type', {
    //     is: Joi.string().custom((value) => normalizeString(value) === normalizeString('Police_Clearance'), 'normalize comparison'),
    //     then: Joi.date().required().label('Issued date').messages({
    //         'any.required': '{{#label}} is required for Police Clearance',
    //     }),
    // }),
    expiresAt: Joi.date()
        .optional()
        .label('Expiry date')
    // .messages({
    //     'date.base': '{{#label}} must be a valid date',
    // }),
});

export const updateLabourProfileSchema = Joi.object({
    draft: Joi.boolean().optional().default(true).label('Draft'),
    isFirstUpdate: Joi.boolean().optional().default(true).label('First update'),
    firstName: Joi.string()
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.string().required().label('First name'),
            otherwise: Joi.string()
                .when('draft', {
                    is: false,
                    then: Joi.string().required().label('First name'),
                    otherwise: Joi.string().optional().allow('').label('First name'),
                }),
        })
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    lastName: Joi.string()
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.string().required().label('Last name'),
            otherwise: Joi.string()
                .when('draft', {
                    is: false,
                    then: Joi.string().required().label('Last name'),
                    otherwise: Joi.string().optional().allow('').label('Last name'),
                }),
        })
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    email: Joi.string()
        .email()
        .optional()
        .messages({
            'string.email': '{{#label}} must be a valid email address',
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    phone: Joi.string()
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.string().required().label('Phone'),
            otherwise: Joi.string()
                .when('draft', {
                    is: false,
                    then: Joi.string().required().label('Phone'),
                    otherwise: Joi.string().optional().allow('').label('Phone'),
                }),
        })
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        }),
    certificates: Joi.array()
        .items(certificateSchema)
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.array().optional().label('Certificates').default([]),
            otherwise: Joi.array()
                .when('draft', {
                    is: false,
                    then: Joi.array().min(1).required().label('Certificates'),
                    otherwise: Joi.array().optional().label('Certificates').default([]),
                }),
        })
        .messages({
            'array.min': '{{#label}} must have at least {{#limit}} item(s)',
            'array.base': '{{#label}} must be an array',
            'any.required': '{{#label}} is required',
        }),
    identification: Joi.array()
        .optional()
        .items(identificationSchema)
        .when('isFirstUpdate', {
            is: true,
            then: Joi.array()
                .min(2)
                .required()
                .label('Identification')
                .custom((value, helpers) => {
                    const idTypes = value.map((id: any) => normalizeString(id.type));
                    const requiredTypes = ['Photo_ID', 'Police_Clearance'].map(normalizeString);
                    for (const type of requiredTypes) {
                        if (!idTypes.includes(type)) {
                            const originalType = Object.keys(statusTicket.requiredIdTypes).find(
                                (key) => normalizeString(key) === type
                            );
                            return helpers.error('any.required', {
                                field: statusTicket.requiredIdTypes[originalType],
                            });
                        }
                    }
                    for (const id of value) {
                        if (normalizeString(id.type) === normalizeString('Police_Clearance') && id.issuedAt) {
                            const sixMonthsAgo = new Date();
                            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                            if (new Date(id.issuedAt) < sixMonthsAgo) {
                                return helpers.error('any.invalid', {
                                    field: 'Police Clearance',
                                    month: 6,
                                    message: 'policeClearanceExpired',
                                });
                            }
                        }
                        if (normalizeString(id.type) === normalizeString('Full Medical') && id.issuedAt) {
                            const twelveMonthsAgo = new Date();
                            twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12)
                            if (new Date(id.issuedAt) < twelveMonthsAgo) {
                                return helpers.error('any.invalid', {
                                    field: 'Full Medical',
                                    month: 12,
                                    message: 'policeClearanceExpired'
                                })
                            }
                        }
                    }
                    return value;
                }, 'identificationValidation'),
            otherwise: Joi.array()
                .when('draft', {
                    is: false,
                    then: Joi.array()
                        .min(1)
                        .required()
                        .label('Identification')
                        .custom((value, helpers) => {
                            if (!value || !Array.isArray(value)) {
                                return helpers.error('any.required', { label: 'Identification' });
                            }
                            if (value.length < 1) {
                                return helpers.error('array.min', { limit: 1 });
                            }
                            return value;
                        }, 'identificationValidation'),
                    otherwise: Joi.array().optional().label('Identification').default([]),
                }),
        })
        .messages({
            'array.min': '{{#label}} must contain at least {{#limit}} item(s)',
            'array.base': '{{#label}} must be an array',
            'any.required': '{{#label}} is required',
            'any.invalid': '{{#label}} is invalid',
        }),
    experienceSummary: Joi.string()
        .optional()
        .messages({
            'string.empty': '{{#label}} is empty',
            'any.required': '{{#label}} is required',
        })
        .when('draft', {
            is: false,
            then: Joi.string().required().label('Experience summary'),
            otherwise: Joi.string().optional().allow('').label('Experience summary'),
        }),
    occupation: Joi.string()
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.string().required().label('occupation'),
            otherwise: Joi.string()
                .when('draft', {
                    is: false,
                    then: Joi.string().min(1).required().label('occupation'),
                    otherwise: Joi.string().optional().allow('').label('occupation'),
                }),
        })
        .messages({
            'any.required': '{{#label}} is required',
            'string.empty': '{{#label}} must be one of the allowed occupations',
        }),
    tickets: Joi.array()
        .items(Joi.string().valid(...Object.keys(statusTicket.tickets)))
        .optional()
        .when('isFirstUpdate', {
            is: true,
            then: Joi.array()
                .min(1)
                .required()
                .label('Tickets'),
            otherwise: Joi.array()
                .when('draft', {
                    is: false,
                    then: Joi.array()
                        .min(1)
                        .required()
                        .label('Tickets'),
                    otherwise: Joi.array().optional().label('Tickets').default([]),
                }),
        })
        .messages({
            'array.min': '{{#label}} must have at least {{#limit}} item(s)',
            'array.base': '{{#label}} must be an array',
            'any.required': '{{#label}} is required',
            'any.only': '{{#label}} must be one of the allowed values',
        }),
    skills: Joi.array()

        .items(
            Joi.string()
                .custom((value, helpers) => {
                    if (!allowedSkills.includes(value.toLowerCase())) {
                        return helpers.error('any.only', { value });
                    }
                    return value;
                }, 'Case-insensitive skills validation')
        )
        .optional()
        .label('Skills')
        .default([])
        .messages({
            'array.base': '{{#label}} must be an array',
            'any.only': 'Invalid skills: {{#value}}',
        }),
    profileImage: Joi.string()
        .optional()
        .label('Profile image')
        .messages({
            'string.empty': '{{#label}} is empty',
        }),
    gender: Joi.string()
        .valid(...Object.values(statusTicket.Gender))
        .optional()
        .allow('')
        .label('Gender')
        .messages({
            'any.only': '{{#label}} must be one of the allowed values',
        }),
    date_of_birth: Joi.date()
        .optional()
        .label('Date of birth')
        .messages({
            'date.base': '{{#label}} must be a valid date',
        }),
    notificationPreferences: Joi.array()
        .items(Joi.string().valid(...Object.values(NotificationPreferenceEnum)))
        .optional()
        .label('Notification preferences')
        .default(['EMAIL', 'IN_APP'])
        .messages({
            'array.base': '{{#label}} must be an array',
            'any.only': '{{#label}} contains invalid notification type',
        }),
}).unknown(true);